import { defineStore } from 'pinia';
import { computed, ref, markRaw } from 'vue';
import {
  CreateProfileRequest,
  NewProfile,
  Profile,
  ProfilePatch,
  UpdateProfileRequest,
} from '@ilteducation/profile-types';
import { create as createApi, destroy as destroyApi, getProfile, getProfiles, update as updateApi } from './api';
import { Cursor } from './types';

const useMyProfileStore = defineStore(
  'my-profile',
  () => {
    const current = ref<Profile | undefined>(undefined);
    const profiles = ref<Record<string, Profile | undefined | null>>({});
    const isLoading = ref(false);
    const cursor = ref<Cursor | undefined>(undefined);
    const hasMore = computed(() => cursor.value !== null);

    const put = (newProfiles: Profile[]) => {
      newProfiles.forEach((newProfile) => {
        if (newProfile) {
          profiles.value[newProfile.id] = markRaw(newProfile);
        }
      });
    };

    const pull = (profileId: string) => {
      const newProfiles = { ...profiles.value };
      delete newProfiles[profileId];
      profiles.value = newProfiles;
    };

    const $reset = () => {
      profiles.value = {};
      current.value = undefined;
      cursor.value = undefined;
    };

    const reset = () => {
      $reset();
    };

    const getById = async (id: string | number): Promise<Profile | undefined> => {
      isLoading.value = true;
      try {
        const profile = await getProfile(id);
        if (profile === null) {
          return undefined;
        }
        put([profile]);
        return profile;
      } catch (error) {
        console.error('Failed to get profile:', error);
        return undefined;
      } finally {
        isLoading.value = false;
      }
    };

    const load = async (context: string, limit = 10): Promise<void> => {
      if (!hasMore.value && isLoading.value) {
        return;
      }

      try {
        // reset list each time we load as we don't keep track of context.
        profiles.value = {};
        isLoading.value = true;
        const { data, cursor: c } = await getProfiles(context, cursor.value as string | undefined, limit);
        put(data);
        cursor.value = c;
      } catch (error) {
        console.error('Failed to load profiles:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const create = async (params: Omit<NewProfile, 'realm'>): Promise<void> => {
      isLoading.value = true;
      try {
        const createdParams: CreateProfileRequest = {
          ...params,
        };
        const profile = await createApi(createdParams);
        put([profile]);
        current.value = profile;
      } catch (error) {
        console.error('Failed to create profile:', error);
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    const update = async (id: string, params: ProfilePatch): Promise<void> => {
      isLoading.value = true;
      try {
        const updateParams: UpdateProfileRequest = {
          ...params,
        };
        const profile = await updateApi(id, updateParams);
        put([{ ...profile, id }]);
        current.value = profile;
      } catch (error) {
        console.error('Failed to update profile:', error);
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    const remove = async (profileId: string): Promise<void> => {
      isLoading.value = true;
      try {
        await destroyApi(profileId);
        pull(profileId);
        current.value = undefined;
      } catch (error) {
        console.error('Failed to remove profile:', error);
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    return {
      profiles,
      current,
      isLoading,
      load,
      getById,
      create,
      update,
      remove,
      reset,
    };
  },
  {
    // @ts-ignore Part of persistence plugin
    persist: true,
  },
);

export default useMyProfileStore;
